import { useCallback, useState } from 'react';
import { useNotificationContext } from 'contexts/NotificationContext';
import useSalesforceTaskUpdate from 'hooks/useSalesforceTaskUpdate';
 
/**
 * Shared logic for saving a single Salesforce task field independently of
 * the rest of the drawer: tracks a loading flag, calls the update API,
 * propagates the refreshed task up via onTaskUpdated, and shows
 * success/error notification.
 **/

const useTaskFieldUpdate = ({
	task,
	field,
	successMessage,
	errorMessage,
	onTaskUpdated,
}) => {
	const { updateTask } = useSalesforceTaskUpdate();
	const { showNotification } = useNotificationContext();
	const [loading, setLoading] = useState(false);
 
	const save = useCallback(
		async (payloadValue) => {
			if (!task) return undefined;
 
			setLoading(true);
 
			try {
				const updatedTask = await updateTask({
					taskId: task.id,
					[field]: payloadValue,
				});
 
				await onTaskUpdated(updatedTask);
 
				const message =
					typeof successMessage === 'function'
						? successMessage(updatedTask)
						: successMessage ?? `Task ${field} updated successfully`;
 
				showNotification(message, 'success');
 
				return updatedTask;
			} catch (err) {
				const message =
					typeof errorMessage === 'function'
						? errorMessage(err)
						: err.message || errorMessage || `Unable to update task ${field}`;
 
				showNotification(message, 'error');
 
				// Re-throw so the caller can decide how to handle it
				throw err;
			} finally {
				setLoading(false);
			}
		},
		[task, field, successMessage, errorMessage, updateTask, onTaskUpdated, showNotification]
	);
 
	return { save, loading };
}

export default useTaskFieldUpdate;
